import { useRef, useState } from 'react';
import { parseGradient, formatGradient } from '../../utils/gradientCss';
import { parseCssColor, formatCssColor } from '../../utils/cssColor';
import IconRemove from '../../components/icons/IconRemove';
import language from '../../variables/language';

// Pixels the pointer may move before a press counts as a drag, not a click.
const DRAG_THRESHOLD_PX = 4;

// Structured editor for the app's 4 gradient tokens: type (linear/radial),
// angle or shape, and an arbitrary number of color stops - edited directly
// on an interactive bar (drag handles to reposition, click empty space to
// add a stop) rather than a stack of numeric-input rows, closer to how
// Figma/Photoshop gradient editors work. Falls back to a plain text input
// when the value doesn't match either shape gradientCss.js actually parses
// - editing stays possible, just without the structured controls, rather
// than silently discarding an unusual value.
//
// All bar interaction goes through the bar's own pointer handlers with pointer
// capture, so one press is one gesture:
//  - press on a handle, release without moving  -> select that stop only
//  - press on a handle and move                 -> move that stop, keep the list sorted
//  - press on empty bar, release without moving -> add a stop there
//  - press on empty bar and move                -> nothing
// (Earlier a handle's click bubbled to the bar and added a stop, and dragging
// relied on window listeners holding a stale snapshot of the stops.)
export default function GradientEditor({ value, onChange }) {
  const t = language[language.lang].themeSettings;
  const parsed = parseGradient(value);
  const [rawFallback, setRawFallback] = useState(value);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const barRef = useRef(null);
  const gestureRef = useRef(null);
  // Stops as they are during an ongoing drag. The parent value only catches up
  // on the next render, so moves in between are applied on top of this list.
  const liveStopsRef = useRef(null);

  if (!parsed) {
    return (
      <div className="gradient-editor gradient-editor-fallback">
        <div className="gradient-editor-preview" style={{ background: value }} />
        <input
          type="text"
          className="theme-editor-text-input"
          value={rawFallback}
          onChange={(e) => { setRawFallback(e.target.value); onChange(e.target.value); }}
        />
      </div>
    );
  }

  const update = (patch) => onChange(formatGradient({ ...parsed, ...patch }));

  const updateStopAt = (index, patch) => {
    const stops = parsed.stops.map((s, i) => (i === index ? { ...s, ...patch } : s));
    update({ stops });
  };

  const positionFromClientX = (clientX) => {
    const rect = barRef.current.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(100, Math.round(ratio * 100)));
  };

  const nearestStopColor = (position) => {
    let closest = parsed.stops[0];
    let closestDist = Infinity;
    for (const s of parsed.stops) {
      const dist = Math.abs((s.position ?? 0) - position);
      if (dist < closestDist) { closest = s; closestDist = dist; }
    }
    return closest ? closest.color : '#ffffff';
  };

  const addStopAt = (position) => {
    const added = { color: nearestStopColor(position), position };
    const stops = [...parsed.stops, added].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    update({ stops });
    setSelectedIndex(stops.indexOf(added));
  };

  // Moves one stop during a drag. Keeps the list sorted by position and
  // follows the moved stop to its new index, so the gesture keeps editing it.
  const moveStop = (index, position) => {
    const base = liveStopsRef.current || parsed.stops;
    const moved = { ...base[index], position };
    const next = base.map((s, i) => (i === index ? moved : s))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const newIndex = next.indexOf(moved);
    liveStopsRef.current = next;
    gestureRef.current.index = newIndex;
    setSelectedIndex(newIndex);
    onChange(formatGradient({ ...parsed, stops: next }));
  };

  const handleBarPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const handle = e.target.closest ? e.target.closest('[data-stop-index]') : null;
    const isHandle = !!handle;
    const index = isHandle ? Number(handle.dataset.stopIndex) : null;
    if (isHandle) setSelectedIndex(index);
    gestureRef.current = {
      isHandle,
      index,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    barRef.current.setPointerCapture(e.pointerId);
  };

  const handleBarPointerMove = (e) => {
    const g = gestureRef.current;
    if (!g) return;
    if (!g.moved) {
      const dist = Math.hypot(e.clientX - g.startX, e.clientY - g.startY);
      if (dist < DRAG_THRESHOLD_PX) return;
      g.moved = true;
    }
    if (g.isHandle) moveStop(g.index, positionFromClientX(e.clientX));
  };

  const endGesture = (e, commit) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    liveStopsRef.current = null;
    if (barRef.current && barRef.current.hasPointerCapture(e.pointerId)) {
      barRef.current.releasePointerCapture(e.pointerId);
    }
    if (commit && g && !g.moved && !g.isHandle) {
      addStopAt(positionFromClientX(e.clientX));
    }
  };

  const removeStop = (index) => {
    if (parsed.stops.length <= 2) return;
    update({ stops: parsed.stops.filter((_, i) => i !== index) });
    setSelectedIndex(Math.max(0, index - 1));
  };

  const activeIndex = Math.min(selectedIndex, parsed.stops.length - 1);
  const activeStop = parsed.stops[activeIndex];
  const { hex, alpha } = parseCssColor(activeStop.color);

  return (
    <div className="gradient-editor">
      <div className="gradient-editor-controls">
        <div className="gradient-editor-type-row">
          <div className="gradient-editor-type-toggle">
            <span
              className={parsed.type === 'linear' ? 'active' : ''}
              onClick={() => parsed.type !== 'linear' && update({ type: 'linear', angle: 90 })}
            >
              {t.gradientLinear}
            </span>
            <span
              className={parsed.type === 'radial' ? 'active' : ''}
              onClick={() => parsed.type !== 'radial' && update({ type: 'radial', shape: 'circle' })}
            >
              {t.gradientRadial}
            </span>
          </div>
          {parsed.type === 'linear' ? (
            <label className="gradient-editor-angle">
              <input
                type="range"
                min="0"
                max="360"
                value={parsed.angle}
                onChange={(e) => update({ angle: Number(e.target.value) })}
              />
              <span>{parsed.angle}°</span>
            </label>
          ) : (
            <select
              className="gradient-editor-shape-select"
              value={parsed.shape}
              onChange={(e) => update({ shape: e.target.value })}
            >
              <option value="circle">{t.gradientCircle}</option>
              <option value="ellipse">{t.gradientEllipse}</option>
            </select>
          )}
        </div>

        <div className="gradient-editor-bar-checker">
          <div
            className="gradient-editor-bar"
            ref={barRef}
            style={{ background: value, touchAction: 'none' }}
            onPointerDown={handleBarPointerDown}
            onPointerMove={handleBarPointerMove}
            onPointerUp={(e) => endGesture(e, true)}
            onPointerCancel={(e) => endGesture(e, false)}
          >
            {parsed.stops.map((stop, index) => (
              <span
                key={index}
                data-stop-index={index}
                className={'gradient-editor-handle' + (index === activeIndex ? ' active' : '')}
                style={{ left: (stop.position ?? 0) + '%', background: stop.color }}
              />
            ))}
          </div>
        </div>
        <p className="gradient-editor-hint">{t.gradientBarHint}</p>

        <div className="gradient-editor-active-stop">
          <input type="color" value={hex} onChange={(e) => updateStopAt(activeIndex, { color: formatCssColor(e.target.value, alpha) })} />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={alpha}
            onChange={(e) => updateStopAt(activeIndex, { color: formatCssColor(hex, Number(e.target.value)) })}
          />
          <input
            type="number"
            min="0"
            max="100"
            className="gradient-editor-position"
            value={activeStop.position != null ? activeStop.position : ''}
            onChange={(e) => updateStopAt(activeIndex, { position: e.target.value === '' ? null : Number(e.target.value) })}
          />
          <span className="gradient-editor-percent">%</span>
          <span
            className={'gradient-editor-remove-stop' + (parsed.stops.length <= 2 ? ' disabled' : '')}
            onClick={() => removeStop(activeIndex)}
          >
            <IconRemove />
          </span>
        </div>
      </div>
    </div>
  );
}
